import {
  AnalysisItem,
  AnalysisType,
  GroupType,
  projectAnalysis,
  ProjectAnalysisEvents,
  UnitType,
} from "@/types/analysis/ProjectAnalysis";
import { ColorDefinition, DataVisualization } from "@/types/colors";
import { ViewerIds } from "@/types/SelectionManager";
import { AppSettingType, FeatureManagementType } from "@api/apiClient";
import BarChart, { BarChartApi } from "@components/Charts/BarChart";
import useAppStore from "@hooks/useAppStore";
import { useBenchmarkOverlays } from "@hooks/useBenchmarkOverlays";
import i18n from "@i18n/i18n";
import parseNumber from "@utils/parseNumber";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";

export interface ByTotalCO2eBarChartProps {
  width: number | undefined;
  height: number | undefined;
  emitter: Emitter<Record<EventType, unknown>>;
}

const ByTotalCO2eBarChart = observer((props: ByTotalCO2eBarChartProps) => {
  const appStore = useAppStore();
  const overlays = useBenchmarkOverlays(AnalysisType.Co2eIntensity);

  const [chartColors, setChartColors] = useState<ColorDefinition[]>(
    DataVisualization.Colors.TotalCo2Palette.toReversed()
  );
  const [chartData, setChartData] = useState<any[]>([["", ""], ["", 0]]);
  const [chartOptions, setChartOptions] = useState<any>({
    title: i18n.t("barchart.title.header"),
    hAxis: { title: i18n.t("results-tab.title.x-axis.scenarios") },
    vAxis: { title: i18n.t("results-tab.title.y-axis.totalCo2") },
  });
  const [hasChartData, setHasChartData] = useState(false);
  const [slantedText, setSlantedText] = useState(false);

  const chartRef = useRef<BarChartApi | null>(null);

  useEffect(() => { updateChartData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);
    return () => projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);
    return () => projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    return () => props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateChartData);
    return () => projectAnalysis.emitter.off(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateChartData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") updateViewers(event.groupKey);
    else if (event.key !== "bar-chart") updateSelection(event.groupKey);
  };

  const updateChartSelections = async () => {
    const groupKeys = projectAnalysis.getSelections(AnalysisType.Co2eIntensity);
    chartRef.current?.setSelection({ legends: Array.from(groupKeys as Set<string>) });
  };

  const updateSelection = (groupKey?: number | string) => {
    chartRef.current?.setSelection({ rowIndex: Number(groupKey) });
  };

  const updateChartData = () => {
    const xAxisTitle = projectAnalysis.isGroupTypeModel
      ? i18n.t("results-tab.title.x-axis.models")
      : i18n.t("results-tab.title.x-axis.scenarios");

    const analysisData = projectAnalysis.getAnalysis(AnalysisType.Co2eIntensity);

    if (!analysisData) {
      setChartColors(DataVisualization.Colors.MaterialsPalette);
      setChartData([[xAxisTitle, ""], ["", 0]]);
      setHasChartData(false);
      return;
    }

    const keys = analysisData.getItemNames();
    const newChartData: any[] = [[xAxisTitle, ...keys]];

    analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
      scenarioAnalysis.groupData.forEach((groupData) => {
        const convertedValues = Array.from(groupData.items.values()).map((item: AnalysisItem) => {
          let v = item.co2eTotal;
          if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
            v = projectAnalysis.applyUnitConversion(item.co2eTotal, scenarioAnalysis);
          }
          return parseNumber(
            i18n.t("number", { value: v, digits: AppSettingType.NumberFormat_EC_Digits }).replace(",", ""),
            i18n.language
          );
        });
        newChartData.push([groupData.name, ...convertedValues]);
      });
    });

    let yAxisTitle = i18n.t("results-tab.title.y-axis.totalCo2");
    if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
      switch (projectAnalysis.selectedUnitType) {
        case UnitType.KgCO2ePerM2:       yAxisTitle = i18n.t("results-tab.title.y-axis.totalCo2-m2");      break;
        case UnitType.KgCO2ePerM2PerYear: yAxisTitle = i18n.t("results-tab.title.y-axis.totalCo2-m2-year"); break;
        default: break;
      }
    }

    setChartColors(analysisData.colorDefinitions().reverse());
    setSlantedText(projectAnalysis.groupType === GroupType.Model);
    setHasChartData(true);
    setChartOptions({
      title: i18n.t("barchart.title.header"),
      hAxis: { title: xAxisTitle, slantedText: projectAnalysis.groupType === GroupType.Model },
      vAxis: { title: yAxisTitle },
    });
    setChartData(newChartData);
  };

  const updateViewers = async (groupKey?: number): Promise<number | undefined> => {
    if (groupKey) {
      const analysisData = projectAnalysis.getAnalysis(AnalysisType.Co2eIntensity);
      if (analysisData) {
        analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
          scenarioAnalysis.modelGroupCadIdsMap.forEach((groupCadIds, urn) => {
            (async () => {
              const viewerId = appStore.selectionManager.getViewerIdByScenarioId(scenarioAnalysis.scenarioId)!;
              if (viewerId === ViewerIds.Viewer1) {
                await appStore.lmViewer1?.isolateByCadIds(groupCadIds.get(groupKey) || [], urn);
              } else {
                await appStore.lmViewer2?.isolateByCadIds(groupCadIds.get(groupKey) || [], urn);
              }
            })();
          });
        });
      }
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }
    return groupKey;
  };

  const handleChartSelection = async (viewerIndex: number, colIndex?: number, _groupKey?: string) => {
    await updateViewers(colIndex);
    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, {
      key: "bar-chart",
      eventType: ResultsEvents.SELECTION_CHANGED,
      viewerIndex,
      groupKey: colIndex,
    } as ResultsEvent);
  };

  return (
    <>
      {hasChartData && (
        <BarChart
          ref={chartRef}
          id="bar-chart"
          width={props.width}
          height={props.height}
          data={chartData}
          colors={chartColors}
          chartOptions={chartOptions}
          slantedText={slantedText}
          overlays={overlays}
          onSelect={(index, legend) => handleChartSelection(-1, index, legend)}
        />
      )}
    </>
  );
});

export default ByTotalCO2eBarChart;
